import {useEffect, useState} from 'react';
import {Lock, Pencil, Plus, Trash2} from 'lucide-react';
import {PhoneHQButton, PhoneHQDialog, PhoneHQInput} from 'ch3-ui-lib';
import {useSetPriceLimitMutation} from '../../store/features/CallRestrictions/callRestrictionsService';
import type {PriceLimits, ScopeId} from '../../store/features/CallRestrictions/callRestrictionsTypes';
import {isCallRestrictionsLocked, priceLimitAboveOf, readApiError} from '../../utils/callRestrictions/apiErrors';
import {Card, LabeledSelect, lucide} from './parts';
import {aboveAllowedMessage, parsePriceLimit, perMinute, scopeKindOf, ScopeOption} from './scopes';

/** A group or person with a limit of its own (`priceLimitPerMinute` on its scope). */
export interface ScopePriceLimit {
    scopeId: ScopeId;
    name: string;
    perMinute: number;
}

interface PriceLimitCardProps {
    limits: PriceLimits;
    /** `company.priceLimitPerMinute`. */
    companyLimit: number | null;
    scoped: ScopePriceLimit[];
    readOnly: boolean;
    groups: ScopeOption[];
    people: ScopeOption[];
    onLocked: () => void;
}

type Editing = {scopeId: ScopeId | ''; value: string; isNew: boolean};

const valueText = (value: number | null) => (value === null ? '' : value.toFixed(2));

/** "Price limit": the company limit, the PhoneHQ ceiling, and group or person limits. */
export const PriceLimitCard = ({limits, companyLimit, scoped, readOnly, groups, people, onLocked}: PriceLimitCardProps) => {
    const {currency, maximumPerMinute: maxPerMinute} = limits;
    const [company, setCompany] = useState(valueText(companyLimit));
    const [companyError, setCompanyError] = useState<string>();
    const [editing, setEditing] = useState<Editing>();
    const [dialogError, setDialogError] = useState<string>();
    const [setPriceLimit, {isLoading}] = useSetPriceLimitMutation();

    useEffect(() => setCompany(valueText(companyLimit)), [companyLimit]);

    const save = async (scopeId: ScopeId, input: string, onError: (message: string) => void): Promise<boolean> => {
        const parsed = parsePriceLimit(input, maxPerMinute, currency);
        if (parsed.error !== undefined) {
            onError(parsed.error);
            return false;
        }
        try {
            await setPriceLimit({scopeId, perMinute: parsed.value}).unwrap();
            return true;
        } catch (e) {
            if (isCallRestrictionsLocked(e)) {
                onLocked();
                return false;
            }
            const above = priceLimitAboveOf(e);
            onError(above
                ? aboveAllowedMessage(above.allowedMaximum ?? maxPerMinute, above.currency ?? currency)
                : readApiError(e)?.message ?? 'Couldn’t save the limit. Try again.');
            return false;
        }
    };

    const companyDirty = company.trim() !== valueText(companyLimit);
    const usedScopes = new Set(scoped.map((l) => l.scopeId));
    const choices = [...groups, ...people].filter((o) => editing?.isNew ? !usedScopes.has(o.scopeId) : true);

    const submitDialog = async () => {
        if (!editing?.scopeId) {
            setDialogError('Choose a group or person.');
            return;
        }
        if (!editing.value.trim()) {
            setDialogError('Enter a limit per minute.');
            return;
        }
        if (await save(editing.scopeId, editing.value, setDialogError)) {
            setEditing(undefined);
        }
    };

    return (
        <Card
            title="Price limit"
            description="The most your company pays per minute for a call or forward. Numbers that cost more are refused. Prices come from your PhoneHQ price list."
            testId="price-limit-card"
        >
            <div className="flex flex-wrap items-end gap-4">
                <div className="flex flex-[0_1_240px] items-end gap-2">
                    <div className="flex-1">
                        <PhoneHQInput
                            label="Whole company"
                            inputMode="decimal"
                            placeholder={maxPerMinute !== null ? maxPerMinute.toFixed(2) : 'No limit'}
                            value={company}
                            disabled={readOnly}
                            error={companyError}
                            suffixIcon={<span className="text-footnote text-ink-muted-strong">{currency ? `${currency} / min` : '/ min'}</span>}
                            className="pr-[88px]"
                            onChange={(e) => {
                                setCompany(e.target.value);
                                setCompanyError(undefined);
                            }}
                        />
                    </div>
                </div>
                {!readOnly && companyDirty && (
                    <PhoneHQButton variant="secondary" disabled={isLoading} data-testid="price-limit-save"
                                   onClick={() => void save('company', company, setCompanyError)}>
                        Save limit
                    </PhoneHQButton>
                )}
                {maxPerMinute !== null && (
                    <div className="flex flex-[1_1_260px] items-center gap-2 text-footnote text-ink-muted-strong">
                        <Lock size={16} aria-hidden="true" className={lucide}/>
                        <span>PhoneHQ allows up to <b className="font-semibold text-ink">{perMinute(maxPerMinute, currency)}</b> for your company. You can set a lower limit.</span>
                    </div>
                )}
            </div>
            <div className="flex flex-col border-0 border-t border-solid border-surface-muted">
                {scoped.map((limit) => (
                    <div key={limit.scopeId} data-testid="scope-price-limit" className="flex min-h-11 items-center gap-3 border-0 border-b border-solid border-surface-muted py-1">
                        <span className="flex-1 text-callout text-ink">
                            {limit.name} <span className="font-normal text-ink-muted">· {scopeKindOf(limit.scopeId) === 'group' ? 'Group' : 'Person'}</span>
                        </span>
                        <span className="text-callout text-ink">{perMinute(limit.perMinute, currency)}</span>
                        {!readOnly && (
                            <>
                                <button type="button" aria-label={`Edit ${limit.name} price limit`}
                                        onClick={() => {
                                            setDialogError(undefined);
                                            setEditing({scopeId: limit.scopeId, value: limit.perMinute.toFixed(2), isNew: false});
                                        }}
                                        className="flex size-7 cursor-pointer items-center justify-center rounded-chip text-ink-secondary hover:bg-surface-muted">
                                    <Pencil size={16} aria-hidden="true" className={lucide}/>
                                </button>
                                <button type="button" aria-label={`Remove ${limit.name} price limit`}
                                        onClick={() => void save(limit.scopeId, '', setCompanyError)}
                                        className="flex size-7 cursor-pointer items-center justify-center rounded-chip text-ink-secondary hover:bg-surface-muted">
                                    <Trash2 size={16} aria-hidden="true" className={lucide}/>
                                </button>
                            </>
                        )}
                    </div>
                ))}
                {!readOnly && (
                    <PhoneHQButton variant="tertiary" size="sm" className="mt-2 self-start"
                                   onClick={() => {
                                       setDialogError(undefined);
                                       setEditing({scopeId: '', value: '', isNew: true});
                                   }}>
                        <Plus size={16} aria-hidden="true" className={lucide}/>
                        Limit for a group or person
                    </PhoneHQButton>
                )}
            </div>

            <PhoneHQDialog
                open={!!editing}
                onOpenChange={(open) => !open && setEditing(undefined)}
                title={editing?.isNew ? 'Limit for a group or person' : 'Edit price limit'}
            >
                {editing && (
                    <div className="flex flex-col gap-4">
                        <LabeledSelect label="Group or person" value={editing.scopeId} disabled={!editing.isNew}
                                       onChange={(e) => setEditing({...editing, scopeId: e.target.value})}>
                            <option value="">Choose a group or person</option>
                            {choices.map((o) => (
                                <option key={o.scopeId} value={o.scopeId}>{`${o.name} · ${scopeKindOf(o.scopeId) === 'group' ? 'Group' : 'Person'}`}</option>
                            ))}
                        </LabeledSelect>
                        <PhoneHQInput
                            label="Limit per minute"
                            inputMode="decimal"
                            value={editing.value}
                            suffixIcon={<span className="text-footnote text-ink-muted-strong">{currency ? `${currency} / min` : '/ min'}</span>}
                            className="pr-[88px]"
                            onChange={(e) => setEditing({...editing, value: e.target.value})}
                        />
                        {dialogError && <p role="alert" className="m-0 text-footnote text-danger-ink">{dialogError}</p>}
                        <div className="flex justify-end gap-2">
                            <PhoneHQButton variant="tertiary" onClick={() => setEditing(undefined)}>Cancel</PhoneHQButton>
                            <PhoneHQButton disabled={isLoading} onClick={() => void submitDialog()}>Save limit</PhoneHQButton>
                        </div>
                    </div>
                )}
            </PhoneHQDialog>
        </Card>
    );
};
